import { z } from 'zod';
import { motorNaoTratado, type Motor } from '@remoto/protocolo';

import { combinarModelosOpencode, modeloPadraoOpencode } from '../opencode/modelos';

/**
 * Modelos oferecidos pelo painel. `fable` cobra créditos extras sem perguntar
 * quando a cota estoura (docs de model-config), por isso exige confirmação
 * explícita; `best` e `opusplan` ficam de fora justamente por escolherem sozinhos.
 */
export const MODELOS = [
  /*
    Os Opus vão por ID EXPLÍCITO, e não pelo apelido `opus`. O apelido é do
    Claude Code e muda de dono a cada versão: no SDK 0.3.260 `opus` é o Opus 5,
    no 0.3.281 já é o Opus 5.5 (medido nas strings do binário em 2026-09-24).
    Com o apelido, o rótulo "Opus 5" passaria a mentir no dia em que o daemon
    atualizasse, sem teste nenhum reclamar. O id explícito diz sempre a mesma
    coisa. O `[1m]` é o sufixo do Claude Code para a janela de 1M de contexto.
  */
  { id: 'claude-opus-5-5', rotulo: 'Opus 5.5', padrao: true, exigeConfirmacao: false, legado: false },
  { id: 'claude-opus-5-5[1m]', rotulo: 'Opus 5.5 (contexto 1M)', padrao: false, exigeConfirmacao: false, legado: false },
  { id: 'claude-opus-5', rotulo: 'Opus 5', padrao: false, exigeConfirmacao: false, legado: false },
  { id: 'sonnet', rotulo: 'Sonnet 5', padrao: false, exigeConfirmacao: false, legado: false },
  { id: 'haiku', rotulo: 'Haiku 4.5', padrao: false, exigeConfirmacao: false, legado: false },
  { id: 'fable', rotulo: 'Fable 5.1 (pode gerar cobrança extra)', padrao: false, exigeConfirmacao: true, legado: false },
  /*
    LEGADO: sessões já gravadas com o apelido continuam válidas (retomar,
    trocar esforço, recomeçar a mestre), mas o apelido sai das listas — o que
    ele significa depende da versão do Claude Code da máquina, e a tela não
    tem como saber qual é.
  */
  { id: 'opus', rotulo: 'Opus (apelido do Claude Code)', padrao: false, exigeConfirmacao: false, legado: true },
  { id: 'opus[1m]', rotulo: 'Opus (apelido do Claude Code, 1M)', padrao: false, exigeConfirmacao: false, legado: true },
] as const;

export type IdModelo = (typeof MODELOS)[number]['id'];

export const ModeloSchema = z.enum(
  MODELOS.map((m) => m.id) as [IdModelo, ...IdModelo[]],
);

/** O que as telas oferecem: a política menos os apelidos legados. */
export const MODELOS_OFERECIDOS = MODELOS.filter((m) => !m.legado);

export const MODELO_PADRAO: IdModelo = 'claude-opus-5-5';
export const MODELO_WORKERS: IdModelo = 'sonnet';

export const EsforcoSchema = z.enum(['low', 'medium', 'high', 'xhigh', 'max']);
export type Esforco = z.infer<typeof EsforcoSchema>;

export const ESFORCO_SESSAO: Esforco = 'high';
export const ESFORCO_WORKERS: Esforco = 'medium';

/**
 * Recebe `string`, e não `IdModelo`, desde que o eixo motor existe.
 *
 * Alargar o parâmetro é o que impede o conserto barato: com `IdModelo`, todo
 * chamador que passasse um modelo do Codex não compilaria, e a saída óbvia
 * seria um `as IdModelo` na borda — que faz a chamada compilar, **desliga a
 * guarda do Fable sem nenhum teste reclamar** e some dentro de um arquivo de
 * quatrocentas linhas. Com `string`, um id que não é da casa cai no `?? false`
 * por caminho declarado, não por conversão forçada.
 *
 * `false` fora de `MODELOS` é a resposta certa e não uma degradação: o campo
 * existe porque o **Fable** cobra crédito extra sem perguntar (regra de
 * segurança da casa). Modelo de outro motor não tem essa propriedade — e
 * inventar uma confirmação para ele treinaria a pessoa a clicar em "confirmo"
 * sem ler, que é justamente o que esvaziaria a guarda do Fable.
 */
export function exigeConfirmacao(modelo: string): boolean {
  return MODELOS.find((m) => m.id === modelo)?.exigeConfirmacao ?? false;
}

const TODOS_OS_ESFORCOS: Esforco[] = ['low', 'medium', 'high', 'xhigh', 'max'];

/** A forma mínima do que a máquina reporta (`ModeloDisponivel` do protocolo). */
export interface ModeloDaMaquina {
  id: string;
  resolvido?: string;
  /** Só o Codex traz: o catálogo dele É a política, então o rótulo vem junto. */
  rotulo?: string;
  aceitaEsforco?: boolean;
  esforcos?: Esforco[];
  /** Os níveis que não cabem no enum (`ultra`, medido em M0-3). */
  esforcosDoMotor?: string[];
  /** O `isDefault` da máquina. */
  padrao?: boolean;
  esforcoPadrao?: string | null;
}

export interface ModeloCombinado {
  /**
   * `string`, não `IdModelo`: o id de um modelo do Codex (`gpt-6-astra`) é da
   * OpenAI e nunca vai estar em `MODELOS`. Quem garante que um esforço inválido
   * não chega ao CLI é o `SdkIniciarSchema`, que recusa por motor — não este
   * tipo. Deixar o tipo estreito aqui só empurraria o `as` para a borda.
   */
  id: string;
  rotulo: string;
  exigeConfirmacao: boolean;
  /** Vazio = este modelo não aceita seletor de esforço. */
  esforcos: string[];
  /** Marcado pela máquina (Codex) ou pela política da casa (Claude). */
  padrao: boolean;
  esforcoPadrao: string | null;
}

/**
 * Cruza a política da casa com a capacidade da máquina — e os dois motores
 * fazem isso de maneiras OPOSTAS, de propósito.
 *
 * **CLAUDE**: a política manda. Modelo que a máquina reporta e `MODELOS` não
 * oferece fica de fora — capacidade nunca alarga política. A máquina refina: os
 * níveis de esforço vêm do `supportedModels()` dela, por modelo. Máquina calada
 * devolve os cinco níveis — oferecer demais não machuca, o CLI rebaixa sozinho
 * e a verdade volta pelo próximo `init`.
 *
 * **CODEX**: a máquina manda, porque não existe política nossa para listar.
 * `MODELOS` é a lista da Anthropic; o catálogo do Codex vem de `model/list`
 * (M0-3: seis modelos, `gpt-6-astra` marcado `isDefault`), e a regra da casa
 * proíbe inventar nome de modelo. Máquina calada devolve **lista vazia**, e
 * isso não é falha: é a tela desabilitando "criar" com um recado, em vez de
 * oferecer `opus` para o app-server da OpenAI.
 *
 * **OPENCODE** (F16): o catálogo do OpenRouter, pela API — a regra mora em
 * `opencode/modelos.ts`, e aqui fica só o despacho.
 *
 * A assimetria é exatamente o motivo de o motor ser parâmetro obrigatório: sem
 * ele, o ramo errado devolveria o catálogo do outro CLI sem erro nenhum.
 */
export function combinarModelos(daMaquina: ModeloDaMaquina[], motor: Motor): ModeloCombinado[] {
  switch (motor) {
    case 'CLAUDE':
      return combinarModelosClaude(daMaquina);
    case 'CODEX':
      return combinarModelosCodex(daMaquina);
    case 'OPENCODE':
      return combinarModelosOpencode(daMaquina);
    default:
      return motorNaoTratado(motor);
  }
}

function combinarModelosCodex(daMaquina: ModeloDaMaquina[]): ModeloCombinado[] {
  return daMaquina.map((m) => ({
    id: m.id,
    // Sem rótulo da máquina fica o id CRU. Id feio é melhor que nome
    // inventado, e a regra da casa é clara sobre qual dos dois é pior.
    rotulo: m.rotulo ?? m.id,
    // Nenhum modelo de fora da casa cobra crédito extra por conta própria;
    // a confirmação existe para o Fable, e só.
    exigeConfirmacao: false,
    esforcos: m.esforcosDoMotor ?? [],
    padrao: m.padrao === true,
    esforcoPadrao: m.esforcoPadrao ?? null,
  }));
}

function combinarModelosClaude(daMaquina: ModeloDaMaquina[]): ModeloCombinado[] {
  return MODELOS_OFERECIDOS.map((politica) => {
    const reportado = daMaquina.find((m) => m.id === politica.id || m.resolvido === politica.id);
    const esforcos =
      reportado === undefined
        ? TODOS_OS_ESFORCOS
        : reportado.aceitaEsforco === false
          ? []
          : reportado.esforcos && reportado.esforcos.length > 0
            ? reportado.esforcos
            : TODOS_OS_ESFORCOS;
    return {
      id: politica.id as string,
      rotulo: politica.rotulo as string,
      exigeConfirmacao: politica.exigeConfirmacao as boolean,
      esforcos: esforcos as string[],
      padrao: politica.padrao as boolean,
      esforcoPadrao: reportado?.esforcoPadrao ?? null,
    };
  });
}

/**
 * Qual modelo a tela deve vir marcando — e `undefined` é resposta legítima.
 *
 * No CLAUDE é `MODELO_PADRAO`, decidido pela casa e independente de máquina:
 * mesmo com a máquina calada o painel sabe o que oferecer.
 *
 * No CODEX sai do `isDefault` que a máquina reportou, e **nada mais**. Sem
 * catálogo, ou com catálogo em que ninguém está marcado, devolve `undefined` —
 * nunca "o primeiro da lista", porque a ordem de `model/list` é da OpenAI e não
 * promessa nenhuma. Escolher pela ordem seria o código decidindo o modelo
 * padrão do outro motor, que é precisamente o que a regra proíbe.
 *
 * No OPENCODE há política da casa (a D13), então nunca é `undefined`: o que o
 * catálogo marcou, ou o padrão da decisão (`opencode/modelos.ts`).
 */
export function modeloPadrao(motor: Motor, daMaquina: ModeloDaMaquina[]): string | undefined {
  switch (motor) {
    case 'CLAUDE':
      return MODELO_PADRAO;
    case 'CODEX':
      return daMaquina.find((m) => m.padrao === true)?.id;
    case 'OPENCODE':
      return modeloPadraoOpencode(daMaquina);
    default:
      return motorNaoTratado(motor);
  }
}
